/**
 * Medidas corporales (Req. 17; tarea 19, 0.4.1): varias a la vez para una
 * fecha, una de cada tipo por día, editar, borrar y ocultar las que no se
 * usan. Las longitudes se guardan en cm con lo que se escribió (Req. 5.4); la
 * grasa corporal, en %. Las tablas existen desde el esquema 1: no hay
 * migración.
 */

import { db, actualizarSettings, leerSettings } from './schema';
import { enEscritura } from './tx';
import type { Measurement, TipoMedida } from './types';
import { ahoraIso, esFechaLocal, type FechaLocal } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import { fechaCorta } from '@/domain/hoy';
import { NOMBRE_MEDIDA, NOTA_MEDIDA_MAX, TIPOS_DE_MEDIDA, esLongitud, esTipoMedida, medidaLegible, revisarValorDeMedida } from '@/domain/medidas';
import type { UnidadLongitud } from '@/domain/units';

/**
 * Las medidas que se pueden leer, de la más reciente a la más antigua. Las
 * que traen la fecha, el tipo o el valor dañados (de un respaldo editado a
 * mano) no se muestran ni cuentan: siguen guardadas y se cuentan aparte.
 * LECTURA PURA.
 */
export async function leerMedidas(): Promise<Measurement[]> {
  const medidas = (await db.measurements.toArray()).filter(medidaLegible);
  return medidas.sort((a, b) => b.fecha.localeCompare(a.fecha) || String(b.creadoEn).localeCompare(String(a.creadoEn)));
}

/** Cuántas medidas guardadas no se pueden leer. LECTURA PURA. */
export async function contarMedidasIlegibles(): Promise<number> {
  return db.measurements.filter((m) => !medidaLegible(m)).count();
}

/** LECTURA PURA. */
export async function leerMedida(id: string): Promise<Measurement | undefined> {
  const m = await db.measurements.get(id);
  return m !== undefined && medidaLegible(m) ? m : undefined;
}

/** Una medida a guardar: lo escrito y en qué unidad (la grasa, en %; la unidad no cuenta). */
export interface MedidaACapturar {
  /** Lo crea la pantalla al abrirse: dos toques no duplican. */
  id: string;
  tipo: TipoMedida;
  valor: number;
  unidad: UnidadLongitud;
}

export interface CapturaDeMedidas {
  fecha: FechaLocal;
  /** Va en cada medida de la captura. */
  nota: string;
  medidas: readonly MedidaACapturar[];
}

function revisarFechaYNota(fecha: FechaLocal, nota: string, hoy: FechaLocal): string | null {
  if (!esFechaLocal(fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (fecha > hoy) throw new ErrorValidacion('Las medidas no pueden quedar en un día que todavía no llega.', 'fecha');
  const limpia = nota.trim();
  if (limpia.length > NOTA_MEDIDA_MAX) throw new ErrorValidacion(`La nota puede tener hasta ${NOTA_MEDIDA_MAX} caracteres.`, 'nota');
  return limpia === '' ? null : limpia;
}

/** El valor canónico y lo capturado de una medida, o el error de su campo. */
function armar(m: Pick<MedidaACapturar, 'tipo' | 'valor' | 'unidad'>): Pick<Measurement, 'valor' | 'capturado'> {
  if (!esTipoMedida(m.tipo)) throw new ErrorValidacion('Ese tipo de medida no existe.');
  const r = revisarValorDeMedida(m.valor, m.tipo, m.unidad);
  if (!r.valido) throw new ErrorValidacion(r.error, m.tipo);
  return { valor: r.canonico, capturado: esLongitud(m.tipo) ? { valor: m.valor, unidad: m.unidad } : null };
}

/**
 * Guarda varias medidas de una fecha (Req. 17.2). Si ya hay una del mismo
 * tipo ese día, se reemplaza su valor (la pantalla lo avisa antes) y conserva
 * su id y cuándo se creó: hay una de cada tipo por día. Todo o nada: si una
 * no sirve, no se guarda ninguna. ESCRIBE.
 */
export async function guardarMedidas(c: CapturaDeMedidas, hoy: FechaLocal): Promise<Measurement[]> {
  const nota = revisarFechaYNota(c.fecha, c.nota, hoy);
  if (c.medidas.length === 0) throw new ErrorValidacion('Escribe al menos una medida.');
  const tipos = new Set(c.medidas.map((m) => m.tipo));
  if (tipos.size !== c.medidas.length) throw new ErrorValidacion('Cada medida va una vez.');
  // Se revisan todas antes de abrir la transacción (Req. 1.4)
  const armadas = c.medidas.map((m) => ({ m, ...armar(m) }));
  return enEscritura([db.measurements], async () => {
    const ahora = ahoraIso();
    const guardadas: Measurement[] = [];
    for (const { m, valor, capturado } of armadas) {
      const existente = await db.measurements.where('[tipo+fecha]').equals([m.tipo, c.fecha]).first();
      const medida: Measurement = {
        id: existente?.id ?? m.id,
        fecha: c.fecha,
        tipo: m.tipo,
        valor,
        capturado,
        nota,
        creadoEn: existente?.creadoEn ?? ahora,
        actualizadoEn: ahora,
      };
      await db.measurements.put(medida);
      guardadas.push(medida);
    }
    return guardadas;
  });
}

export interface CambiosDeMedida {
  fecha: FechaLocal;
  valor: number;
  unidad: UnidadLongitud;
  nota: string;
}

/**
 * Corrige una medida (Req. 17.5). Si la fecha nueva ya tiene una de ese
 * tipo, no se junta: se pide corregir o borrar esa. ESCRIBE.
 */
export async function corregirMedida(id: string, cambios: CambiosDeMedida, hoy: FechaLocal): Promise<Measurement> {
  const nota = revisarFechaYNota(cambios.fecha, cambios.nota, hoy);
  return enEscritura([db.measurements], async () => {
    const actual = await db.measurements.get(id);
    if (actual === undefined || !medidaLegible(actual)) throw new ErrorValidacion('Esa medida ya no existe.');
    const { valor, capturado } = armar({ tipo: actual.tipo, valor: cambios.valor, unidad: cambios.unidad });
    if (cambios.fecha !== actual.fecha) {
      const otra = await db.measurements
        .where('[tipo+fecha]')
        .equals([actual.tipo, cambios.fecha])
        .filter((x) => x.id !== id)
        .first();
      if (otra !== undefined) {
        throw new ErrorValidacion(
          `Ya tienes ${NOMBRE_MEDIDA[actual.tipo].toLocaleLowerCase('es-MX')} del ${fechaCorta(cambios.fecha).toLocaleLowerCase('es-MX')}: corrige esa o bórrala primero.`,
          'fecha',
        );
      }
    }
    const medida: Measurement = { ...actual, fecha: cambios.fecha, valor, capturado, nota, actualizadoEn: ahoraIso() };
    await db.measurements.put(medida);
    return medida;
  });
}

/** Borra una medida (Req. 17.5); la pantalla ya pidió confirmación. ESCRIBE. */
export async function borrarMedida(id: string): Promise<void> {
  await enEscritura([db.measurements], async () => {
    await db.measurements.delete(id);
  });
}

/**
 * Oculta o vuelve a mostrar un tipo (Req. 17.3). Ocultarlo no borra sus
 * medidas: dejan de pedirse al capturar y su historial sigue a la mano. ESCRIBE.
 */
export async function ocultarMedida(tipo: TipoMedida, oculta: boolean): Promise<void> {
  if (!esTipoMedida(tipo)) throw new ErrorValidacion('Ese tipo de medida no existe.');
  const actuales = new Set((await leerSettings()).medidasOcultas);
  if (oculta) actuales.add(tipo);
  else actuales.delete(tipo);
  // En el orden de siempre, para que el respaldo no cambie por el orden de los toques
  await actualizarSettings({ medidasOcultas: TIPOS_DE_MEDIDA.filter((t) => actuales.has(t)) });
}
